import { useState } from 'react';

import { Input, InputWrapperProps } from '@any-ui-react/core';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { $getRoot } from 'lexical';
import { tv } from 'tailwind-variants';

import { SlotsToClasses } from '@anyx/shared-ui';


import {
  TrackingLinkPlugin,
  ToolbarPlugin,
  TrackingLinkComposerContext,
  TrackingLinkComposerContextType,
  OnBlurPlugin,
  TrackingLinkNodes,
  EmojiPlugin,
  FontSizePlugin,
  BoldPlugin,
  Alignment,
  DEFAULT_FONT_SIZE,
  DEFAULT_ALIGNMENT,
  AlignmentPlugin,
  VariableNode,
  VariablePlugin,
  VariablePluginProvider,
  VariablePluginOption,
} from './extensions';

const theme = {
  link: 'text-link',
};

// Catch any errors that occur during Lexical updates and log them
// or throw them as needed. If you don't throw them, Lexical will
// try to recover gracefully without losing user data.
function onError(error: Error) {
  console.error(error);
}

export type CrmRichTextEditorInlineState = {
  weighted: boolean;
  fontSize: string;
  align: Alignment;
};

const styles = tv({
  slots: {
    root: 'relative h-max overflow-hidden rounded-sm border bg-white',
    content: 'text-gray-9 overflow-auto px-3 py-2 text-xs outline-none',
  },
  variants: {
    error: {
      true: {
        root: 'border-failure',
      },
      false: {
        root: 'hover:not-focus-within:border-gray-5 border-gray-2 focus-within:border-blue-6',
      },
    },
    size: {
      xs: {
        content: 'max-h-28 min-h-10',
      },
      sm: {
        content: 'max-h-48 min-h-32',
      },
      md: {
        content: 'max-h-80 min-h-60',
      },
    },
  },
});

type CrmRichTextEditorSlots = keyof ReturnType<typeof styles>;

interface CrmRichTextEditorProps extends TrackingLinkComposerContextType {
  wrapper?: Omit<InputWrapperProps, 'children'>;
  placeholder?: string;
  editable?: boolean;
  withTrackingLink?: boolean;
  initialEditorState?: string;
  initialInlineState?: Partial<CrmRichTextEditorInlineState>;
  onChange?: (
    values: {
      richText: string;
      text: string;
    } & CrmRichTextEditorInlineState
  ) => void;
  withInlineState?: boolean;
  variablePluginOptions?: VariablePluginOption[];
  classNames?: SlotsToClasses<CrmRichTextEditorSlots>;
  size?: 'xs' | 'sm' | 'md';
}

export function CrmRichTextEditor({
  wrapper,
  placeholder,
  editable,
  withTrackingLink,
  initialEditorState,
  initialInlineState,
  onChange,
  insertTrackingLink,
  withInlineState,
  classNames,
  size = 'md',
  variablePluginOptions,
}: CrmRichTextEditorProps) {
  const initialConfig: InitialConfigType = {
    namespace: 'CrmRichTextEditor',
    editorState: initialEditorState || undefined,
    nodes: withTrackingLink ? [VariableNode, ...TrackingLinkNodes] : [VariableNode],
    theme,
    onError,
    editable,
  };

  const [inlineState, setInlineState] = useState<CrmRichTextEditorInlineState>({
    weighted: initialInlineState?.weighted ?? false,
    fontSize: initialInlineState?.fontSize ?? DEFAULT_FONT_SIZE,
    align: initialInlineState?.align ?? DEFAULT_ALIGNMENT,
  });

  const { error } = wrapper || {};

  const classes = styles({
    error: !!error,
    size,
  });

  return (
    <Input.Wrapper
      {...wrapper}
      classNames={{
        error: 'mt-1',
      }}
    >
      <div className={classes.root({ className: classNames?.root })}>
        <LexicalComposer initialConfig={initialConfig}>
          <VariablePluginProvider options={variablePluginOptions}>
            <TrackingLinkComposerContext.Provider
              value={{
                insertTrackingLink,
              }}
            >
              {editable && (
                <ToolbarPlugin
                  withInlineState={withInlineState}
                  withVariable={(variablePluginOptions?.length || 0) > 0}
                  {...inlineState}
                />
              )}
              <PlainTextPlugin
                contentEditable={
                  <ContentEditable
                    className={classes.content({ className: classNames?.content })}
                  />
                }
                placeholder={
                  <div className="text-gray-5 absolute top-2 left-3 text-sm">{placeholder}</div>
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
              <OnChangePlugin
                onChange={(editorState) => {
                  onChange?.({
                    text: editorState.read(() => {
                      const root = $getRoot();
                      return root.getTextContent();
                    }),
                    richText: JSON.stringify(editorState.toJSON()),
                    weighted: inlineState.weighted,
                    fontSize: inlineState.fontSize,
                    align: inlineState.align,
                  });
                }}
              />
              <OnBlurPlugin />
              <EmojiPlugin />
              <FontSizePlugin
                onChange={(fontSize) => {
                  setInlineState((prev) => ({ ...prev, fontSize }));
                }}
              />
              <BoldPlugin
                onChange={(weighted) => {
                  setInlineState((prev) => ({ ...prev, weighted }));
                }}
              />
              <AlignmentPlugin
                onChange={(align) => {
                  setInlineState((prev) => ({ ...prev, align }));
                }}
              />
              <VariablePlugin />
              {withTrackingLink && <TrackingLinkPlugin />}
              <HistoryPlugin />
              <AutoFocusPlugin />
            </TrackingLinkComposerContext.Provider>
          </VariablePluginProvider>
        </LexicalComposer>
      </div>
    </Input.Wrapper>
  );
}
